// @flow

// Libs
import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import _find from 'lodash/find'
import _isEqual from 'lodash/isEqual'
import _size from 'lodash/size'
import _first from 'lodash/first'
import _values from 'lodash/values'

// Components
import Button from '@react-mdc/button'
import DataTable from './DataTable'
import Add from 'fc-icons/src/Add'
import Delete from 'fc-icons/src/Delete'
import AddComment from './AddComment'
import DeleteComments from './DeleteComments'

// Constants
import {
  PROP_SOIL_JOB_ID,
  PROP_FIELD as PROP_JOB_FIELD,
  PROP_STATUS,
  RESULTS_POSTED,
} from 'hfty-model-soil-jobs'

import {
  PROP_ASSET_ID,
  PROP_LABEL,
} from 'fc-model-assets'

import {
  PROP_FIELD as PROP_COMMENT_FIELD,
} from 'hfty-model-comments'

// messages
import {
  messages,
} from 'hfty-comments'

// Container
import commentsManagerContainer from '../containers/CommentsManager'

// Types
import type {
  SoilJob,
} from 'hfty-model-soil-jobs/types'

import type {
  Asset,
} from 'fc-model-assets/types'

import type {
  CommentOperationsById,
} from 'hfty-operator-comments/types'

import type {
  Crop,
} from 'fc-model-crops/types'

// Styles
import { dark, lightGrey, grey } from 'hfty-styles/src/colors'

const styles = {
  container: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    width: 300,
    backgroundColor: '#333',
    color: '#fff',
    boxShadow: '-2 0 2px rgba(0, 0, 0, 0.2)',
  },
  button: {
    cursor: 'pointer',
    padding: '2px 7px',
  },
  header: {
    padding: 10,
    backgroundColor: lightGrey(0.8),
    display: 'flex',
    alignItems: 'center',
  },
  titleWrapper: {
    margin: '0 20px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  heading: {
    fontSize: '1.4em',
    color: grey(),
  },
  headingItem: {
    fontWeight: 400,
    fontSize: '0.9em',
    color: grey(0.6),
    marginLeft: 15,
    marginTop: 5,
  },
  itemTitle: {
    fontWeight: 500,
    marginRight: 5,
  },
  notAvailable: {
    margin: '40px auto',
    padding: '0 10px',
    fontSize: '1.4em',
    color: grey(0.9),
  },
  datatableContainer: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
  },
}

// Types
type Props = {
  selectedIds: Array<number>,
  match: Object,
  jobs: Array<SoilJob>,
  crops: Array<Crop>,
  fields: Array<Asset>,
  currentUserUnits: string,
  recommendationTypeOptions: Array<Object>,
  operations: CommentOperationsById,
  actions: {
    onAdd: Function,
    onDelete: Function,
    fetchComments: Function,
    onChangeOperation: Function,
    onCancelOperations: Function,
    onSubmitOperations: Function,
  }
}

// Exports
export class CommentsManager extends Component {
  props: Props

  constructor () {
    super()
    this.onAdd = this.onAdd.bind(this)
    this.onDelete = this.onDelete.bind(this)
  }

  componentDidMount () {
    const { match, jobs } = this.props
    const jobId = Number(match.params.jobId)
    const job = _find(jobs, (job: SoilJob): boolean => job[PROP_SOIL_JOB_ID] === jobId) || {}
    this.fetchComments(job)
  }

  componentWillReceiveProps (nextProps: Props) {
    if (
      !_isEqual(nextProps.match.params.jobId, this.props.match.params.jobId) ||
      !_isEqual(nextProps.jobs, this.props.jobs)) {
      const jobId = Number(nextProps.match.params.jobId)
      const job = _find(nextProps.jobs, (job: SoilJob): boolean => job[PROP_SOIL_JOB_ID] === jobId) || {}
      this.fetchComments(job)
    }
  }

  fetchComments (job: SoilJob) {
    if (job[PROP_SOIL_JOB_ID]) {
      this.props.actions.fetchComments({jobId: job[PROP_SOIL_JOB_ID]})
    }
  }

  canDelete (): boolean {
    return this.props.selectedIds.length > 0
  }

  canAdd (): boolea {
    return _size(this.props.crops) > 0
  }

  onAdd () {
    const { jobs, match } = this.props
    const jobId = Number(match.params.jobId)
    const job = _find(jobs, (job: SoilJob): boolean => job[PROP_SOIL_JOB_ID] === jobId) || {}
    this.canAdd() && this.props.actions.onAdd({[PROP_COMMENT_FIELD]: job[PROP_JOB_FIELD]})
  }

  onDelete () {
    this.canDelete() && this.props.actions.onDelete()
  }

  render (): React.Element<any> {
    const { jobs, fields, crops, operations, currentUserUnits } = this.props
    const jobId = Number(this.props.match.params.jobId)

    const job = _find(jobs, (job: SoilJob): boolean => job[PROP_SOIL_JOB_ID] === jobId) || {}
    const field = _find(fields, (field: Asset): boolean => field[PROP_ASSET_ID] === job[PROP_JOB_FIELD]) || {}

    return (
      <div
        data-test='hfty-comments-dom/CommentsManager'
        style={styles.container}
      >
        <div style={styles.header}>
          <Link to={`/`}>
            <Button
              raised
              dense
              primary
            >
              <FormattedMessage {...messages['back']} />
            </Button>
          </Link>
          <div style={styles.titleWrapper}>
            <span style={styles.heading}><FormattedMessage {...messages['comments']} /></span>
            <span style={styles.headingItem}>
              <span style={styles.itemTitle}>Job Id:</span>
              {this.props.match.params.jobId}
            </span>
            <span style={styles.headingItem}>
              <span style={styles.itemTitle}>Field:</span>
              {field ? field[PROP_LABEL] : '--'}
            </span>
          </div>
        </div>
        {job[PROP_STATUS] === RESULTS_POSTED
          ? <div style={styles.datatableContainer}>
            <DataTable>
              <div
                style={styles.button}
                onClick={this.onAdd}
              >
                <Add color={this.canAdd() ? dark() : lightGrey()} />
              </div>
              <div
                style={styles.button}
                onClick={this.onDelete}
              >
                <Delete color={this.canDelete() ? dark() : lightGrey()} />
              </div>
            </DataTable>
            <DeleteComments
              crops={crops}
              fields={fields}
              operations={operations}
              onSubmit={this.props.actions.onSubmitOperations}
              onCancel={this.props.actions.onCancelOperations}
              currentUserUnits={currentUserUnits}
            />
            <AddComment
              crops={crops}
              operation={_first(_values(operations))}
              onSubmit={this.props.actions.onSubmitOperations}
              onCancel={this.props.actions.onCancelOperations}
              onChange={this.props.actions.onChangeOperation}
              currentUserUnits={currentUserUnits}
              recommendationTypeOptions={this.props.recommendationTypeOptions}
            />
          </div>
          : <div style={styles.notAvailable}>
            <FormattedMessage {...messages['commentNotAvailable']} />
          </div>
        }
      </div>
    )
  }
}

export default commentsManagerContainer(CommentsManager)
